// Aritmética de la venta — gemelo en TypeScript de Application/Ventas/CalculoVenta.cs.
//
// Es el mismo cálculo en dos lenguajes por la misma razón que el motor de
// precios: en la caja y en la nube corre en C#, y acá corre en el navegador
// para armar el ticket mientras el cajero carga la mercadería, sin ir y volver
// al servidor por cada línea.
//
// Antes esto vivía suelto: el POS calculaba el importe de la línea a mano
// (`Math.floor((precio * cantidad + 500) / 1000)`) y esa equivalencia con el
// `Math.Round(AwayFromZero)` de C# no la verificaba nadie. Era correcta, y era
// exactamente el tipo de cosa que se rompe en silencio el día que alguien toca
// uno de los dos lados. Ahora las dos implementaciones corren el mismo corpus:
// contratos/venta-golden.json.
//
// Las reglas del archivo, iguales a las del de C#:
//
//   1. Aritmética ENTERA. Centavos y milésimas. JavaScript no tiene decimal,
//      así que la única forma de coincidir con C# es que los dos hagan
//      enteros; donde C# usa `decimal` para no truncar, acá se multiplica
//      antes de dividir.
//   2. Cero dependencias y cero estado.

/**
 * Importe de una línea: precio por cantidad, con la cantidad en milésimas.
 *
 * El medio centavo va PARA ARRIBA (away from zero) y no al par más cercano:
 * con el redondeo del banquero, $10,125 daría $10,12 y $10,135 daría $10,14, y
 * en el mostrador eso se lee como que el sistema cobra distinto para el mismo
 * caso.
 *
 * `floor((n + 500) / 1000)` es exactamente eso para enteros no negativos: el
 * +500 es el medio centavo, y el piso corta el resto.
 */
export function importeLinea(precioUnitarioCentavos: number, cantidadMilesimas: number): number {
  const n = precioUnitarioCentavos * cantidadMilesimas

  // El signo se maneja aparte porque `Math.floor` de un negativo se va para el
  // otro lado: -0,5 tiene que dar -1 y no 0.
  return n < 0 ? -Math.floor((-n + 500) / 1000) : Math.floor((n + 500) / 1000)
}

/**
 * Venta por importe: «dame $2.000 de papa». Devuelve las milésimas que
 * corresponden a ese importe.
 *
 * Se ajusta a los decimales de la unidad y PARA ABAJO: pedir $2.000 de algo
 * que se vende por unidad no puede dar 1,37 unidades, y cobrar de más una
 * unidad que no se entregó es peor que entregar un poco menos de lo pedido.
 */
export function cantidadPorImporte(
  importeCentavos: number,
  precioUnitarioCentavos: number,
  decimales: number,
): number {
  if (precioUnitarioCentavos <= 0) return 0

  const paso = Math.pow(10, 3 - Math.min(3, Math.max(0, decimales)))

  // Se divide UNA vez por (precio × paso) en lugar de dividir dos veces: es el
  // mismo número —floor(a/(b·c)) = floor((a/b)/c)— y así no hay una división
  // intermedia con coma que pueda quedar en 1332,9999.
  return Math.floor((importeCentavos * 1000) / (precioUnitarioCentavos * paso)) * paso
}

/**
 * Redondeo de efectivo. Devuelve el AJUSTE con signo, no el total.
 *
 * Existe porque no hay monedas: un total de $1.487 no se puede pagar ni dar
 * vuelto exacto, y la alternativa real es que el cajero improvise. El empate
 * va PARA ABAJO, a favor del cliente: medio peso no cambia nada para el local
 * y compra el argumento de mostrador.
 */
export function ajustePorRedondeo(totalCentavos: number, pasoCentavos: number): number {
  if (pasoCentavos <= 1) return 0

  const resto = totalCentavos % pasoCentavos
  if (resto === 0) return 0

  return resto * 2 <= pasoCentavos ? -resto : pasoCentavos - resto
}

/**
 * Vuelto, o `null` si lo entregado no alcanza.
 *
 * Null y no un número negativo: un vuelto negativo impreso en un ticket es un
 * error que el cajero no va a notar.
 */
export function vuelto(entregadoCentavos: number, totalCentavos: number): number | null {
  return entregadoCentavos < totalCentavos ? null : entregadoCentavos - totalCentavos
}

/**
 * IVA contenido en un importe que ya lo incluye.
 *
 * Los precios de verdulería son finales, con IVA adentro: el neto se despeja,
 * no se suma. importe = neto × (1 + tasa) ⇒ iva = importe × tasa / (1 + tasa),
 * que en enteros es importe × bps / (10000 + bps).
 */
export function ivaContenido(importeCentavos: number, alicuotaBps: number): number {
  if (alicuotaBps <= 0) return 0

  const numerador = importeCentavos * alicuotaBps
  const denominador = 10000 + alicuotaBps

  // Medio centavo para arriba, en enteros: floor((2a + d) / 2d).
  return Math.floor((2 * numerador + denominador) / (2 * denominador))
}
